import { put, select } from 'redux-saga/effects';
import { ApplicationState } from '../../index';
import { FabricationMockupsService } from '../../../services/FabricationMockups';
import { setNeedToRefreshProjects } from '../fabrication-projects/actions';
import {
  saveMockupFailure,
  saveMockupSuccess,
  loadMockupsFailure,
  loadMockupsSuccess,
  removeMockupFailure,
  removeMockupSuccess,
  setEditingMockup,
  loadMockups,
  editPavementFailure,
  editPavementSuccess,
  IMockupAnalyticsData
} from './actions';
import { IFabricationMockup, IPavement } from './types';
import { addToast } from '../toast/actions';
import { EToastTypes } from '../toast/types';
import { sendTagging } from '../tagging/actions';

interface IMockupSagaParams {
  payload: {
    contractNumber: string;
    mockupId: number;
    mockupName: string;
    hasRoof: boolean;
    hasCovering: boolean;
    typicalFloorPlan: string;
    otherTypicalFloorPlan: string;
    pavements: IPavement[];
    analyticsData?: IMockupAnalyticsData;
  };
}

interface IPavementsSagaParams {
  payload: {
    contractNumber: string;
    pavement: IPavement;
    mockupId: number;
  };
}

export function* saveFabricationMockupSaga(params: IMockupSagaParams) {
  const {
    contractNumber,
    mockupName,
    hasRoof,
    hasCovering,
    typicalFloorPlan,
    otherTypicalFloorPlan,
    pavements,
    mockupId,
    analyticsData
  } = params.payload;

  try {
    const mockupService = new FabricationMockupsService();

    const contracts: ApplicationState['fabricationContracts']['contracts'] =
      yield select(
        (state: ApplicationState) => state.fabricationContracts.contracts
      );
    const bpContratoObra =
      contracts?.find((c) => c.contractNumber === contractNumber)?.soldTo
        .code ?? '';

    if (mockupId) {
      yield mockupService.editFabricationMockup(
        mockupId,
        mockupName,
        hasRoof,
        hasCovering,
        typicalFloorPlan,
        otherTypicalFloorPlan,
        pavements
      );
      yield put(loadMockups(contractNumber));
      yield put(saveMockupSuccess());
      yield put(setNeedToRefreshProjects());
      yield put(
        addToast({
          message: 'Maquete editada com sucesso.',
          type: EToastTypes.SUCCESS,
          durationInMS: 5000
        })
      );
      yield put(
        sendTagging({
          screenName: 'LG:MaquetesEProjetos:Maquetes:EditarMaquete',
          itemName: 'sucess:maquete_editada_com_sucesso',
          event: 'success',
          itemDescription:
            'maquetesEProjetos_maquetes_editarMaquete_maqueteEditadaComSucesso',
          nonInteraction: true,
          extraData: {
            nome_maquete: mockupName,
            id_maquete: `${contractNumber}_${mockupName}`,
            tipo_edicao: analyticsData?.tipoEdicao || '',
            total_pavimentos_apos_edicao: pavements.length
          }
        })
      );
    } else {
      const createdMockup: IFabricationMockup =
        yield mockupService.createFabricationMockup(
          contractNumber,
          mockupName,
          hasRoof,
          hasCovering,
          typicalFloorPlan,
          otherTypicalFloorPlan,
          pavements
        );
      yield put(setEditingMockup(createdMockup.id));
      yield put(saveMockupSuccess(createdMockup));
      yield put(
        addToast({
          message: 'Maquete criada com sucesso.',
          type: EToastTypes.SUCCESS,
          durationInMS: 5000
        })
      );
      yield put(
        sendTagging({
          screenName: 'LG:MaquetesEProjetos:Maquetes:CriarMaquete',
          itemName: 'sucess:maquete_criada_com_sucesso',
          event: 'success',
          itemDescription:
            'maquetesEProjetos_maquetes_criarMaquete_maqueteCriadaComSucesso',
          nonInteraction: true,
          extraData: {
            nome_maquete: mockupName,
            contrato_obra: contractNumber,
            id_maquete: `${contractNumber}_${mockupName}`,
            total_pavimentos: pavements.length,
            pavimentos_acima_terreo: pavements.filter((p) => p.position >= 0)
              .length,
            possui_pavimento_abaixo_terreo: pavements.some(
              (p) => p.position < 0
            ),
            qtd_pavimento_abaixo_terreo: pavements.filter((p) => p.position < 0)
              .length,
            possui_pavimento_tipo: typicalFloorPlan !== '',
            possui_coberta: hasRoof,
            possui_cobertura: hasCovering,
            possui_outro_tipo_pavimento: otherTypicalFloorPlan !== '',
            origem: analyticsData?.origem || 'nova',
            bp_contrato_obra: bpContratoObra,
            ...(analyticsData?.idMaqueteOrigem && {
              id_maquete_origem: analyticsData.idMaqueteOrigem
            })
          }
        })
      );
    }
  } catch (err) {
    yield put(saveMockupFailure(err));
  }
}

export function* loadFabricationMockupSaga(params: IMockupSagaParams) {
  const { contractNumber } = params.payload;

  try {
    const mockupService = new FabricationMockupsService();
    const mockups: IFabricationMockup[] =
      yield mockupService.loadFabricationMockups(contractNumber);
    yield put(loadMockupsSuccess(mockups));
  } catch (err) {
    yield put(
      addToast({
        message: 'Falha ao carregar as maquetes.',
        type: EToastTypes.DANGER
      })
    );
    yield put(loadMockupsFailure(err));
  }
}

export function* removeFabricationMockupSaga(params: IMockupSagaParams) {
  const { mockupId, contractNumber } = params.payload;

  try {
    const mockupService = new FabricationMockupsService();
    yield mockupService.removeFabricationMockup(mockupId);
    yield put(removeMockupSuccess());
    yield put(loadMockups(contractNumber));
    yield put(
      addToast({
        message: 'Maquete removida com sucesso.',
        type: EToastTypes.SUCCESS,
        durationInMS: 5000
      })
    );
  } catch (err) {
    yield put(removeMockupFailure(err));
  }
}

export function* editPavementsSaga(params: IPavementsSagaParams) {
  const { contractNumber, pavement } = params.payload;

  try {
    const mockupService = new FabricationMockupsService();
    const {
      id,
      slabDate,
      beamDate,
      pillarDate,
      slabStatus,
      beamStatus,
      pillarStatus
    } = pavement;
    yield mockupService.editPavements(
      Number(id),
      slabDate,
      beamDate,
      pillarDate,
      slabStatus,
      beamStatus,
      pillarStatus
    );
    yield put(editPavementSuccess(Number(id)));
    yield put(
      addToast({
        message: 'Datas atualizadas com sucesso.',
        type: EToastTypes.SUCCESS,
        durationInMS: 5000
      })
    );
    yield put(loadMockups(contractNumber));
  } catch (err) {
    yield put(editPavementFailure(err));
  }
}
